// src/pages/Users.js
import React, { useState, useEffect, useCallback } from 'react';
import { Plus, Search, Edit2, Trash2, Key, UserCog, RefreshCw, ShieldCheck, Shield, User } from 'lucide-react';
import api from '../utils/api';
import { useToast } from '../contexts/ToastContext';
import { useAuth } from '../contexts/AuthContext';
import { formatDistanceToNow } from 'date-fns';
import { ro } from 'date-fns/locale';

const ROLES = ['superadmin', 'admin', 'manager', 'user'];
const ROLE_ICONS = { superadmin: ShieldCheck, admin: Shield, manager: UserCog, user: User };
const ROLE_COLORS = { superadmin: '#f59e0b', admin: '#4f8ef7', manager: '#22c55e', user: '#8892a4' };

function UserModal({ user, onClose, onSave }) {
  const [form, setForm] = useState(user || { username: '', email: '', password: '', full_name: '', role: 'user', is_active: 1 });
  const [loading, setLoading] = useState(false);
  const toast = useToast();
  const { hasRole } = useAuth();
  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const handleSave = async () => {
    if (!form.username || !form.email) return toast.error('Username și email sunt obligatorii');
    if (!user?.id && !form.password) return toast.error('Parola este obligatorie pentru utilizatori noi');
    setLoading(true);
    try {
      if (user?.id) { await api.put(`/users/${user.id}`, form); toast.success('Utilizator actualizat'); }
      else { await api.post('/users', form); toast.success('Utilizator creat'); }
      onSave();
    } catch (e) { toast.error(e.response?.data?.error || 'Eroare'); }
    finally { setLoading(false); }
  };

  return (
    <div className="modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal modal-md">
        <div className="modal-header">
          <span className="modal-title">{user?.id ? 'Editare Utilizator' : 'Utilizator Nou'}</span>
          <button className="btn-icon" onClick={onClose}><span style={{ fontSize: 18 }}>×</span></button>
        </div>
        <div className="modal-body">
          <div className="form-grid form-grid-2">
            <div className="form-group">
              <label className="form-label">Username *</label>
              <input className="form-input" value={form.username} onChange={e => set('username', e.target.value)} disabled={!!user?.id} placeholder="ion.popescu" />
            </div>
            <div className="form-group">
              <label className="form-label">Email *</label>
              <input className="form-input" type="email" value={form.email} onChange={e => set('email', e.target.value)} placeholder="ion@firma.ro" />
            </div>
            <div className="form-group">
              <label className="form-label">Nume Complet</label>
              <input className="form-input" value={form.full_name || ''} onChange={e => set('full_name', e.target.value)} placeholder="Ion Popescu" />
            </div>
            <div className="form-group">
              <label className="form-label">Rol</label>
              <select className="form-select" value={form.role} onChange={e => set('role', e.target.value)} disabled={!hasRole('superadmin')}>
                {ROLES.map(r => <option key={r} value={r}>{r.charAt(0).toUpperCase() + r.slice(1)}</option>)}
              </select>
            </div>
            {!user?.id && (
              <div className="form-group">
                <label className="form-label">Parolă *</label>
                <input className="form-input" type="password" value={form.password} onChange={e => set('password', e.target.value)} placeholder="Min. 6 caractere" />
              </div>
            )}
            {user?.id && (
              <div className="form-group">
                <label className="form-label">Status</label>
                <select className="form-select" value={form.is_active} onChange={e => set('is_active', parseInt(e.target.value))}>
                  <option value={1}>Activ</option>
                  <option value={0}>Dezactivat</option>
                </select>
              </div>
            )}
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={onClose}>Anulare</button>
          <button className="btn btn-primary" onClick={handleSave} disabled={loading}>
            {loading ? <span className="spinner" /> : 'Salvare'}
          </button>
        </div>
      </div>
    </div>
  );
}

function ResetPasswordModal({ user, onClose }) {
  const [pwd, setPwd] = useState('');
  const [loading, setLoading] = useState(false);
  const toast = useToast();

  const handleReset = async () => {
    if (pwd.length < 6) return toast.error('Parola trebuie să aibă minim 6 caractere');
    setLoading(true);
    try {
      await api.put(`/users/${user.id}/reset-password`, { new_password: pwd });
      toast.success('Parolă resetată cu succes');
      onClose();
    } catch (e) { toast.error(e.response?.data?.error || 'Eroare'); }
    finally { setLoading(false); }
  };

  return (
    <div className="modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal modal-sm">
        <div className="modal-header">
          <span className="modal-title">Reset Parolă — {user.username}</span>
          <button className="btn-icon" onClick={onClose}><span style={{ fontSize: 18 }}>×</span></button>
        </div>
        <div className="modal-body">
          <div className="form-group" style={{ marginBottom: 0 }}>
            <label className="form-label">Parolă Nouă</label>
            <input className="form-input" type="password" value={pwd} onChange={e => setPwd(e.target.value)} placeholder="Min. 6 caractere" autoFocus />
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={onClose}>Anulare</button>
          <button className="btn btn-primary" onClick={handleReset} disabled={loading}>
            {loading ? <span className="spinner" /> : 'Resetare Parolă'}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function Users() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState('');
  const [modal, setModal] = useState(null);
  const [resetModal, setResetModal] = useState(null);
  const { user: me, hasRole } = useAuth();
  const toast = useToast();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (search) params.search = search;
      if (roleFilter) params.role = roleFilter;
      const { data } = await api.get('/users', { params });
      setUsers(data);
    } finally { setLoading(false); }
  }, [search, roleFilter]);

  useEffect(() => { load(); }, [load]);

  const handleDelete = async (u) => {
    if (!window.confirm(`Ștergi utilizatorul "${u.username}"?`)) return;
    try { await api.delete(`/users/${u.id}`); toast.success('Utilizator șters'); load(); }
    catch (e) { toast.error(e.response?.data?.error || 'Eroare'); }
  };

  return (
    <div className="fade-in">
      <div className="page-header">
        <div>
          <div className="page-title">Utilizatori</div>
          <div className="page-subtitle">{users.length} utilizatori înregistrați</div>
        </div>
        <button className="btn btn-primary" onClick={() => setModal('new')}><Plus size={16} /> Utilizator Nou</button>
      </div>

      <div className="filter-bar">
        <div style={{ position: 'relative', flex: 1, minWidth: 200 }}>
          <Search size={15} style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: 'var(--text-secondary)' }} />
          <input className="form-input" style={{ paddingLeft: 32 }} placeholder="Caută utilizatori..." value={search} onChange={e => setSearch(e.target.value)} />
        </div>
        <select className="form-select" style={{ width: 150 }} value={roleFilter} onChange={e => setRoleFilter(e.target.value)}>
          <option value="">Toate rolurile</option>
          {ROLES.map(r => <option key={r} value={r}>{r.charAt(0).toUpperCase() + r.slice(1)}</option>)}
        </select>
        <button className="btn btn-secondary" onClick={load}><RefreshCw size={15} /></button>
      </div>

      <div className="card" style={{ padding: 0 }}>
        {loading ? (
          <div className="loading-center"><span className="spinner" /></div>
        ) : (
          <div className="table-wrapper">
            <table>
              <thead>
                <tr><th>UTILIZATOR</th><th>EMAIL</th><th>ROL</th><th>STATUS</th><th>ULTIMUL LOGIN</th><th>ACȚIUNI</th></tr>
              </thead>
              <tbody>
                {users.map(u => {
                  const RoleIcon = ROLE_ICONS[u.role] || User;
                  return (
                    <tr key={u.id}>
                      <td>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                          <div style={{ width: 32, height: 32, background: `${ROLE_COLORS[u.role]}22`, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 13, fontWeight: 700, color: ROLE_COLORS[u.role], flexShrink: 0 }}>
                            {u.full_name?.[0] || u.username?.[0] || '?'}
                          </div>
                          <div>
                            <div style={{ fontWeight: 600, fontSize: 14 }}>{u.full_name || u.username}</div>
                            {u.full_name && <div style={{ fontSize: 12, color: 'var(--text-secondary)' }}>@{u.username}</div>}
                          </div>
                        </div>
                      </td>
                      <td style={{ fontSize: 13 }}>{u.email}</td>
                      <td>
                        <span style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 13, fontWeight: 600, color: ROLE_COLORS[u.role] }}>
                          <RoleIcon size={13} />{u.role}
                        </span>
                      </td>
                      <td><span className={`badge ${u.is_active ? 'badge-success' : 'badge-neutral'}`}>{u.is_active ? 'Activ' : 'Dezactivat'}</span></td>
                      <td style={{ fontSize: 12, color: 'var(--text-secondary)' }}>
                        {u.last_login ? formatDistanceToNow(new Date(u.last_login), { locale: ro, addSuffix: true }) : 'Niciodată'}
                      </td>
                      <td>
                        <div style={{ display: 'flex', gap: 6 }}>
                          <button className="btn-icon" title="Editare" onClick={() => setModal(u)}><Edit2 size={14} /></button>
                          <button className="btn-icon" title="Reset Parolă" onClick={() => setResetModal(u)}><Key size={14} /></button>
                          {hasRole('superadmin') && u.id !== me?.id && (
                            <button className="btn-icon" title="Ștergere" style={{ color: 'var(--danger)' }} onClick={() => handleDelete(u)}><Trash2 size={14} /></button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {modal && <UserModal user={modal === 'new' ? null : modal} onClose={() => setModal(null)} onSave={() => { setModal(null); load(); }} />}
      {resetModal && <ResetPasswordModal user={resetModal} onClose={() => setResetModal(null)} />}
    </div>
  );
}
